import '@draft-js-plugins/mention/lib/plugin.css';
import '@draft-js-plugins/emoji/lib/plugin.css';
import './Editor.scss';

import classNames from 'classnames';
import { COPY_GENERIC } from '@sonar-software/sonar-ts-constants';
import DraftEditor from '@draft-js-plugins/editor';
import draftToHtml from 'draftjs-to-html';
import { EditOutlined } from '@ant-design/icons';
import { getClosestTableBody } from '@lib/popups';
import groupBy from 'lodash/groupBy';
import { OutsideClickWrapper } from './general';
import { TaggedUser } from '@sonar-software/sonar-ts-types';
import { useEditorToolbar } from '../hooks/useEditorToolbar';
import { UserWithMetadata } from '@sonar-software/sonar-core';
import values from 'lodash/values';

import { Button, Popover } from 'antd';
import {
	ContentState,
	convertFromRaw,
	convertToRaw,
	DraftHandleValue,
	EditorState,
	getDefaultKeyBinding,
	KeyBindingUtil,
	RawDraftContentState
} from 'draft-js';
import { defaultSuggestionsFilter, MentionData } from '@draft-js-plugins/mention';
import { Else, If, Then, When } from 'react-if';
import { getFullName, isJSON } from '@sonar-software/sonar-ts-lib';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useEditorMentions, useUsers } from '@hooks';

export interface EditorRef {
	edit: () => void;
	save: () => void;
}

const { createWithContent, moveSelectionToEnd, push, createEmpty, moveFocusToEnd } = EditorState;
const { createFromText } = ContentState;
const { isSoftNewlineEvent } = KeyBindingUtil;

export function getInitialEditorState(content: string | undefined): EditorState {
	if (typeof content === 'string') {
		const contentState = isJSON(content) ? convertFromRaw(JSON.parse(content)) : createFromText(content);
		const editorState = createWithContent(contentState);

		return moveSelectionToEnd(editorState);
	}

	return createEmpty();
}

function htmlTransform(entity: { type: string; data: MentionData }) {
	if (entity.type === 'mention') {
		const { name } = entity.data.mention;

		return `<span class="mention">@${name}</span>`;
	}

	return undefined;
}

function getUserSuggestions(users: Array<UserWithMetadata>): Array<MentionData> {
	return users
		.filter((user) => !user.isLocked)
		.map((user) => ({
			email: user.email,
			id: user.id,
			name: getFullName(user.firstName, user.lastName),
			userId: user.id
		}));
}

function getTaggedUsers(rawContent: RawDraftContentState) {
	const taggedUsers = groupBy(rawContent.entityMap, 'type').mention?.reduce(
		(tagged: { [key: string]: TaggedUser }, entity) => {
			const { userId, email } = entity.data.mention;

			if (!tagged[ userId ]) {
				tagged[ userId ] = {
					email,
					userId
				};
			}

			return tagged;
		},
		{}
	);

	return values(taggedUsers || []);
}

export function hasContent(editorState: EditorState): boolean {
	const contentState = editorState.getCurrentContent();

	const firstBlock = contentState.getBlockMap().first();
	const hasLeadingStyleElement = firstBlock?.getType() !== 'unstyled';
	const hasText = contentState.hasText();

	return hasText || hasLeadingStyleElement;
}

export interface EditorProps {
	bordered?: boolean;
	canBeEmpty?: boolean;
	className?: string;
	content: string | undefined;
	editing?: boolean;
	editOnClick?: boolean;
	enableMentions?: boolean;
	contentCheck?: (hasContent: boolean) => void;
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	isForm?: boolean;
	isReadOnly?: boolean;
	isSpreadsheet?: boolean;
	onBlur?: () => void;
	onCancel?: () => void;
	onFocus?: () => void;
	onSave: (content: string, html: string, taggedUsers: TaggedUser[]) => void;
	placeholder?: string;
	saveOnBlur?: boolean;
	showEditButton?: boolean;
}

export const Editor = forwardRef<EditorRef | null, EditorProps>(({
	bordered,
	canBeEmpty,
	className,
	content,
	contentCheck,
	editing,
	editOnClick,
	enableMentions = false,
	getPopupContainer,
	isForm,
	isReadOnly,
	isSpreadsheet,
	onBlur,
	onCancel,
	onFocus,
	onSave,
	placeholder,
	saveOnBlur,
	showEditButton
}, ref) => {
	const { plugins, toolbar } = useEditorToolbar();
	const { MentionSuggestions, plugins: modifiedPlugins } = useEditorMentions(plugins, enableMentions);
	const [ editorState, setEditorState ] = useState(getInitialEditorState(content));
	const [ focused, setFocused ] = useState(false);
	const editorRef = useRef<DraftEditor>(null);

	const users = useUsers();
	const [ suggestions, setSuggetions ] = useState<MentionData[]>([]);
	const [ visibleSuggestions, setVisibleSuggestions ] = useState<MentionData[]>(suggestions);
	const [ showSuggestions, setShowSuggestions ] = useState(false);

	useEffect(() => {
		const mappedUsers = getUserSuggestions(users);

		setSuggetions(mappedUsers);
		setVisibleSuggestions(mappedUsers);
	}, [ users ]);

	const onSearchChange = useCallback(
		({ value }: { value: string }) => {
			if (suggestions) {
				setVisibleSuggestions(defaultSuggestionsFilter(value, suggestions));
			}
		},
		[ suggestions ]
	);

	const handleStartEditing = useCallback((focusToEnd?: boolean) => {
		setFocused(true);
		setTimeout(() => {
			if (focusToEnd) {
				setEditorState((state) => moveFocusToEnd(state));
			} else {
				editorRef.current?.focus();
			}
		}, 10);
	}, []);

	const handleSaveChanges = useCallback(() => {
		const rawContent = convertToRaw(editorState.getCurrentContent());
		const html = draftToHtml(rawContent, {}, false, htmlTransform);

		onSave(JSON.stringify(rawContent), html, getTaggedUsers(rawContent));

		if (isForm) {
			setEditorState(EditorState.createEmpty());
		}

		setFocused(false);
		editorRef.current?.blur();
	}, [ editorState, isForm, onSave ]);

	const handleCancelChanges = useCallback(() => {
		editorRef.current?.blur();

		if (onCancel) {
			onCancel();
		}

		setEditorState(isForm ? EditorState.createEmpty() : getInitialEditorState(content));
		setFocused(false);
	}, [ content, isForm, onCancel ]);

	useEffect(() => {
		if (editing) {
			handleStartEditing();
		}
	}, [ editing, handleStartEditing ]);

	useEffect(() => {
		if (content) {
			const contentState = isJSON(content) ? convertFromRaw(JSON.parse(content)) : createFromText(content);
			setEditorState((prevEditorState) => push(prevEditorState, contentState, 'insert-characters'));
		}
	}, [ content ]);

	const editorHasContent = hasContent(editorState);

	const isActive = focused || Boolean(editing);
	const isClickable = !isReadOnly && (isActive || Boolean(isForm) || !editorHasContent);
	const shouldShowEditButton = !isReadOnly && Boolean(showEditButton) && !isClickable;

	useEffect(() => {
		if (contentCheck) {
			contentCheck(editorHasContent);
		}
	}, [ contentCheck, editorHasContent ]);

	const handleGeneralOnClick = useCallback(
		(event: MouseEvent | React.SyntheticEvent<Record<string, unknown>>) => {
			event.preventDefault();
			event.stopPropagation();

			if (!isActive && editOnClick && !isReadOnly) {
				handleStartEditing(event.type === 'focus' && Boolean((event as MouseEvent).relatedTarget));
			}
		},
		[ editOnClick, handleStartEditing, isActive, isReadOnly ]
	);

	const handleOnFocus = (event: React.SyntheticEvent<Record<string, unknown>>) => {
		if (onFocus) {
			onFocus();
		}

		handleGeneralOnClick(event);
	};

	const handleOnClickOutside = useCallback(() => {
		if (isActive && saveOnBlur) {
			handleSaveChanges();
		}
	}, [ handleSaveChanges, isActive, saveOnBlur ]);

	useImperativeHandle(ref, () => ({
		edit: handleStartEditing,
		save: handleSaveChanges
	}));

	const outsideClickClassnames = classNames(
		'draft',
		{
			'active': isActive,
			bordered,
			'editOnClick': editOnClick,
			'empty': !editorHasContent,
			'isForm': isForm,
			'isReadOnly': isReadOnly,
			'outside-click': isSpreadsheet,
			'spreadsheet': isSpreadsheet
		},
		className
	);

	const onKeyCommand = (command: string): DraftHandleValue => {
		if (command === 'enter_command') {
			handleSaveChanges();

			return 'handled';
		}

		return 'not-handled';
	};

	const myKeyBindingFn = (e: React.KeyboardEvent<Element>) => {
		if (e.code === 'Escape') {
			handleCancelChanges();

			return;
		}

		if (isSpreadsheet && e.code === 'Enter' && !isSoftNewlineEvent(e)) {
			return 'enter_command';
		}

		if (isSpreadsheet && e.code === 'Tab') {
			setTimeout(() => handleSaveChanges(), 10);
		}

		return getDefaultKeyBinding(e);
	};

	const draftEditorComponent =
		<>
			<DraftEditor
				editorState={ editorState }
				handleKeyCommand={ onKeyCommand }
				keyBindingFn={ myKeyBindingFn }
				onBlur={ onBlur }
				onChange={ setEditorState }
				onFocus={ handleOnFocus }
				placeholder={ editorHasContent ? '' : placeholder }
				plugins={ modifiedPlugins }
				readOnly={ isReadOnly }
				ref={ editorRef } />
			<MentionSuggestions
				onOpenChange={ setShowSuggestions }
				onSearchChange={ onSearchChange }
				open={ showSuggestions }
				suggestions={ visibleSuggestions } />
			<EditOutlined className='edit-icon' />
		</>;

	return (
		<OutsideClickWrapper
			className={ outsideClickClassnames }
			onClick={ handleGeneralOnClick }
			onClickOutside={ handleOnClickOutside }>
			<If condition={ isSpreadsheet }>
				<Then>
					<Popover
						classNames={ { root: 'editor-popover' } }
						content={ toolbar }
						getPopupContainer={ getPopupContainer ?? getClosestTableBody }
						open={ isActive }
						placement='bottom'>
						{ draftEditorComponent }
					</Popover>
				</Then>

				<Else>
					{ draftEditorComponent }
					<If condition={ isActive }>
						<Then>
							{ toolbar }
							<Button
								className='action'
								disabled={ !canBeEmpty && !editorHasContent }
								onClick={ handleSaveChanges }
								type='primary'>
								{ COPY_GENERIC.SAVE }
							</Button>
							<Button
								className='action'
								onClick={ handleCancelChanges }>
								{ COPY_GENERIC.CANCEL }
							</Button>
						</Then>
						<Else>
							<When condition={ shouldShowEditButton && !editOnClick }>
								<EditOutlined
									className='edit-button'
									onClick={ () => handleStartEditing() } />
							</When>
						</Else>
					</If>
				</Else>
			</If>
		</OutsideClickWrapper>
	);
});
